import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DashboardLayout from '../Layouts/DashboardLayout';
import PageHeader from '../Dashboard/components/PageHeader';

// Import custom icons
import LocationIcon from '../icons/LocationIcon';
import PhoneIcon from '../icons/PhoneIcon';
import EmailIcon from '../icons/EmailIcon';
import ClockIcon from '../icons/ClockIcon';

// Styled Components
const PageContainer = styled(Container)(({ theme }) => ({
  paddingTop: '20px',
  paddingBottom: '40px',
  display: 'flex',
  justifyContent: 'center'
}));

const ContentCard = styled(Paper)(({ theme }) => ({
  borderRadius: '16px',
  padding: '48px',
  width: '100%',
  maxWidth: '900px',
  background: '#FFFFFF',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('md')]: {
    padding: '24px'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '32px',
  color: '#121927',
  marginBottom: '16px',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px'
  }
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  color: '#6B7280',
  textAlign: 'center',
  maxWidth: '600px',
  margin: '0 auto 48px auto',
  lineHeight: 1.6
}));

const ContactItemCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '32px 24px',
  borderRadius: '12px',
  backgroundColor: '#F9FAFB',
  height: '100%',
  transition: 'all 0.3s ease',
  border: '1px solid transparent',
  '&:hover': {
    transform: 'translateY(-5px)',
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
    boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.06)'
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  backgroundColor: '#E0F2FE',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px',
  color: '#0B91D6'
}));

const ItemTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '18px',
  color: '#111827',
  marginBottom: '12px'
}));

const ItemText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '15px',
  color: '#4B5563',
  textAlign: 'center',
  lineHeight: 1.6,
  whiteSpace: 'pre-line'
}));

const StyledLink = styled('a')(({ theme }) => ({
  color: '#0B91D6',
  textDecoration: 'none',
  fontWeight: 500,
  transition: 'color 0.2s',
  '&:hover': {
    color: '#0277BD',
    textDecoration: 'underline'
  }
}));

const ContactUs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const contactDetails = [
    {
      icon: <LocationIcon width={32} height={32} color="#0B91D6" />,
      title: 'Visit Us',
      content: (
        <>
          Office No A-611, 6th floor, Bestech Tower<br />
          Mohali, SAS Nagar, Punjab
        </>
      )
    },
    {
      icon: <PhoneIcon width={32} height={32} color="#0B91D6" />,
      title: 'Call Us',
      content: (
        <Stack spacing={0.5} alignItems="center">
          <StyledLink href="tel:+919815009722">+91-9815009722</StyledLink>
          <StyledLink href="tel:+919115959722">+91-9115959722</StyledLink>
        </Stack>
      )
    },
    {
      icon: <EmailIcon width={32} height={32} color="#0B91D6" />,
      title: 'Email Us',
      content: (
        <StyledLink href="mailto:support@visibeen.com">
          support@visibeen.com
        </StyledLink>
      )
    },
    {
      icon: <ClockIcon width={32} height={32} color="#0B91D6" />,
      title: 'Working Hours',
      content: (
        <>
          Mon-Fri: 9 AM – 6 PM<br />
          Sunday: Closed
        </>
      )
    }
  ];

  return (
    <DashboardLayout>
      <PageHeader
        title="Contact Us"
        subtitle="Get in touch with our team"
      />
      <PageContainer maxWidth="lg">
        <ContentCard>
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                textAlign: 'center',
                color: '#0B91D6',
                fontWeight: 600,
                letterSpacing: '0.1em',
                mb: 1
              }}
            >
              GET IN TOUCH!
            </Typography>
            <SectionTitle>
              Have Questions?
            </SectionTitle>
            <SubTitle>
              Have questions or need support? We're here to help—reach out to our team anytime.
            </SubTitle>
          </Box>

          <Grid container spacing={4}>
            {contactDetails.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <ContactItemCard>
                  <IconWrapper>
                    {item.icon}
                  </IconWrapper>
                  <ItemTitle>
                    {item.title}
                  </ItemTitle>
                  <ItemText as="div">
                    {item.content}
                  </ItemText>
                </ContactItemCard>
              </Grid>
            ))}
          </Grid>
        </ContentCard>
      </PageContainer>
    </DashboardLayout>
  );
};

export default ContactUs;
